import React, { useEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useSearchParams } from "wouter";
import { buildSalesWhatsAppUrl } from "../lib/billingSupport.js";
import { api, formatMoney } from "../lib/api.js";
import { currencyFromLocale, getAnnualInstallmentCents, getPlanPricing } from "../../../shared/plans.js";
import { STONE_INSTALLMENT_COUNT } from "../../../shared/stone.js";
import type { BillingInterval, Plan } from "../../../shared/types.js";
import { isCheckoutOfferKind } from "../../../shared/checkout_offers.js";
import {
  PlanComparisonTable,
  PlanGuaranteeSeal,
  PlanIntervalToggle,
} from "../components/plans/PlanComparisonTable.js";
import { PlanChoiceCards } from "../components/plans/PlanChoiceCards.js";
import { CheckoutOfferBanner } from "../components/CheckoutOfferBanner.js";
import {
  buildCheckoutUrlFromPlans,
  comparisonTableChooseProps,
  plansChooseEventName,
  type PlansChooseSource,
} from "../lib/plansNavigation.js";
import { clarityEvent, claritySet } from "../lib/clarity.js";
import { persistCheckoutOfferToken, offerFailureI18nKey, offerRejectionReason } from "../lib/checkoutOfferSession.js";

interface PublicOffer {
  token: string;
  kind: string;
  locked_plan: Plan | null;
  interval: BillingInterval;
  first_cents: number;
  list_cents: number;
  recurring_cents: number;
}

/**
 * Opens on monthly; a link that names a period keeps it. Opening Brazil on annual was tried on
 * 2026-10-09 and rolled back the same evening, when the afternoon's orders fell by half (all of
 * them monthly) with leads arriving as usual — not proven to be the cause, rolled back to be safe.
 */
function parsePlansInterval(params: URLSearchParams): BillingInterval {
  if (params.get("offer")) return "monthly";
  return params.get("interval") === "annual" ? "annual" : "monthly";
}

/**
 * Step 1 of the checkout flow. The buyer arrives already convinced (PIT-385), so the page only
 * asks for a plan and a period: a one-line header, the period toggle and two whole-card buttons
 * fit a phone screen, with the feature table below for whoever wants to compare.
 */
export function PlansPage() {
  const { t, i18n } = useTranslation();
  const [, setLocation] = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const interval = parsePlansInterval(searchParams);
  const offerToken = searchParams.get("offer");
  const [offer, setOffer] = useState<PublicOffer | null>(null);
  const [offerReady, setOfferReady] = useState(!offerToken);
  const [offerInvalidReason, setOfferInvalidReason] = useState<string | null>(null);
  const embedded = searchParams.get("embed") === "1";
  const salesWhatsAppUrl = useMemo(
    () => buildSalesWhatsAppUrl(t, searchParams.get("email") ?? undefined),
    [t, searchParams],
  );
  const offerBanner = offer && isCheckoutOfferKind(offer.kind) ? offer : null;
  const offerFirstCents = offerBanner ? offerBanner.first_cents : undefined;

  useEffect(() => {
    if (!offerToken) {
      setOffer(null);
      setOfferReady(true);
      persistCheckoutOfferToken(null);
      return;
    }
    persistCheckoutOfferToken(offerToken);
    let cancelled = false;
    setOfferReady(false);
    api<PublicOffer>(`/api/public/offers/${encodeURIComponent(offerToken)}`)
      .then((row) => {
        if (cancelled) return;
        setOfferInvalidReason(null);
        setOffer(row);
        setOfferReady(true);
      })
      .catch((err) => {
        if (cancelled) return;
        const reason = offerRejectionReason(err);
        if (!reason) {
          setOfferReady(true);
          return;
        }
        persistCheckoutOfferToken(null);
        setOffer(null);
        setOfferReady(true);
        setOfferInvalidReason(reason);
        setSearchParams((params) => {
          const next = new URLSearchParams(params);
          next.delete("offer");
          return next;
        });
      });
    return () => {
      cancelled = true;
    };
  }, [offerToken]);

  function setInterval(next: BillingInterval) {
    setSearchParams((params) => {
      const nextParams = new URLSearchParams(params);
      nextParams.set("interval", next);
      return nextParams;
    });
  }

  // A double tap on a card, or a card and then a table button, navigates once.
  const choosingRef = useRef(false);
  function choosePlan(plan: Plan, source: PlansChooseSource) {
    if (choosingRef.current) return;
    choosingRef.current = true;
    const chosenInterval = offerToken ? "monthly" : interval;
    clarityEvent(plansChooseEventName(plan, chosenInterval));
    claritySet("plans_choose_source", source);
    setLocation(buildCheckoutUrlFromPlans(searchParams, plan, chosenInterval));
  }

  return (
    <main className="min-h-screen surface-aurora">
      <div className={`mx-auto max-w-5xl px-4 ${embedded ? "py-4 lg:py-8" : "py-6 lg:py-14"}`}>
        {!embedded && (
          <header className="text-center mb-5 lg:mb-8">
            <h1 className="text-2xl lg:text-4xl font-semibold" style={{ letterSpacing: "-0.02em" }}>
              {t("plansPage.heading")}
            </h1>
            <p className="opacity-80 mt-2 text-sm lg:text-base">{t("plansPage.reassurance")}</p>
          </header>
        )}

        {offerInvalidReason && (
          <aside className="checkout-offer-banner mb-5 text-left" role="status">
            <p className="checkout-offer-banner-body">{t(offerFailureI18nKey(offerInvalidReason))}</p>
          </aside>
        )}

        {offerBanner && (
          <CheckoutOfferBanner
            kind={offerBanner.kind}
            firstCents={offerBanner.first_cents}
            discountCents={Math.max(0, offerBanner.list_cents - offerBanner.first_cents)}
            recurringCents={offerBanner.recurring_cents}
            surface="plans"
            className="mb-5 text-left"
          />
        )}

        {!offerToken && (
          <div className="mb-5 flex items-center justify-center">
            <PlanIntervalToggle value={interval} onChange={setInterval} />
          </div>
        )}

        {offerReady && (
          <>
            <PlanChoiceCards
              interval={interval}
              lockedPlan={offer?.locked_plan ?? undefined}
              offerFirstCents={offerFirstCents}
              onChoose={(plan) => choosePlan(plan, "card")}
            />
            <section aria-labelledby="plans-compare-heading" className="mt-8 lg:mt-14">
              <h2 id="plans-compare-heading" className="sr-only">
                {t("plansPage.compareTitle")}
              </h2>
              <PlanComparisonTable
                interval={interval}
                mode="marketing"
                {...comparisonTableChooseProps(!!offerBanner, choosePlan)}
                lockedPlan={offer?.locked_plan ?? undefined}
                offerFirstCents={offerFirstCents}
              />
              {/* Risk reversal right after the comparison, where the buyer has just weighed the plans. */}
              <PlanGuaranteeSeal />
            </section>
          </>
        )}

        {offerReady && !offerBanner && (
          // Phones: the Premium choice stays at hand while the buyer scrolls the table.
          // Both plans stay at hand while the buyer reads the table: Basic (light, left) and
          // Premium (gradient, right), each with its price for the period on top.
          <div className="plans-sticky">
            <div className="plans-sticky-row">
              {(["basic", "premium"] as const).map((plan) => (
                <div key={plan} className="plans-sticky-col">
                  <span className="plans-sticky-note">
                    {interval === "annual" && currencyFromLocale(i18n.language) === "BRL"
                      ? `${STONE_INSTALLMENT_COUNT}x ${formatMoney(getAnnualInstallmentCents(plan), i18n.language, "BRL")}`
                      : `${formatMoney(
                          getPlanPricing(plan, interval, currencyFromLocale(i18n.language)).totalCents,
                          i18n.language,
                          currencyFromLocale(i18n.language),
                        )}${t(interval === "annual" ? "plans.perYear" : "plans.perMonth")}`}
                  </span>
                  <button
                    type="button"
                    className={`plan-comparison-cta ${plan === "premium" ? "plan-comparison-cta-primary" : "plan-comparison-cta-secondary"}`}
                    onClick={() => choosePlan(plan, "sticky")}
                  >
                    {t("planComparison.chooseShort", { plan: t(`plans.${plan}`) })}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <footer className={`text-center mt-10 text-sm opacity-75 ${offerReady && !offerBanner ? "pb-28" : ""}`}>
          <div className="opacity-70 text-xs">
            <Trans
              i18nKey="plansPage.needHelp"
              components={{
                whatsapp: (
                  <a
                    href={salesWhatsAppUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline underline-offset-2 hover:opacity-100"
                  />
                ),
              }}
            />
          </div>
        </footer>
      </div>
    </main>
  );
}
